import React, { useCallback, useEffect, useRef, useState } from "react";
import { heroSlides, marqueeWords, whatsappLink } from "../data/site";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "./Icons";
import Marquee from "./Marquee";
import {
  btnBase,
  btnGhost,
  btnGhostSweep,
  btnIcon,
  btnLabel,
  btnSolid,
  btnSolidSweep,
  btnSweep,
  cx,
  display,
} from "../lib/ui";

const DURATION = 6800;
const TOTAL = heroSlides.length;

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [prev, setPrev] = useState(null);
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0); // bumped on every change, restarts the timer bar
  const stageRef = useRef(null);
  const touch = useRef({ x: 0, y: 0, active: false });

  const goTo = useCallback((next) => {
    setIndex((current) => {
      const target = (next + TOTAL) % TOTAL;
      if (target === current) return current;
      setPrev(current);
      setCycle((c) => c + 1);
      return target;
    });
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const back = useCallback(() => goTo(index - 1), [goTo, index]);

  /* Autoplay — parked while the pointer rests on the stage, while the tab
     is in the background, and entirely for reduced-motion visitors. */
  useEffect(() => {
    if (paused) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setTimeout(() => goTo(index + 1), DURATION);
    return () => window.clearTimeout(timer);
  }, [index, paused, goTo]);

  useEffect(() => {
    const onVisibility = () => setPaused(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "ArrowRight") next();
      if (event.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, back]);

  /* Pointer parallax: the picture drifts a couple of percent against the
     cursor. Written straight to custom properties to skip re-renders. */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    if (window.matchMedia("(pointer: coarse)").matches) return undefined;

    let frame = 0;
    let target = { x: 0, y: 0 };

    const apply = () => {
      frame = 0;
      stage.style.setProperty("--px", `${target.x}px`);
      stage.style.setProperty("--py", `${target.y}px`);
    };

    const onMove = (event) => {
      const rect = stage.getBoundingClientRect();
      target = {
        x: ((event.clientX - rect.left) / rect.width - 0.5) * -26,
        y: ((event.clientY - rect.top) / rect.height - 0.5) * -18,
      };
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    stage.addEventListener("pointermove", onMove);
    return () => {
      stage.removeEventListener("pointermove", onMove);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, []);

  const onTouchStart = (event) => {
    const t = event.touches[0];
    touch.current = { x: t.clientX, y: t.clientY, active: true };
  };

  const onTouchEnd = (event) => {
    if (!touch.current.active) return;
    const t = event.changedTouches[0];
    const dx = t.clientX - touch.current.x;
    const dy = t.clientY - touch.current.y;
    touch.current.active = false;
    if (Math.abs(dx) > 56 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) next();
      else back();
    }
  };

  const slide = heroSlides[index];

  return (
    <section
      id="home"
      className="relative isolate flex min-h-[100svh] flex-col justify-end overflow-hidden bg-ink"
    >
      {/* ---------- stage ----------
          The incoming picture is unveiled by a curtain rising from the
          floor while the image inside travels the other way, so the
          change reads as a lift rather than a wipe. */}
      <div
        ref={stageRef}
        className="absolute inset-0 -z-10"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {heroSlides.map((item, i) => {
          const isActive = i === index;
          const isPrev = i === prev;
          return (
            <div
              key={item.id}
              className={cx(
                "absolute inset-0",
                isActive ? "z-30 opacity-100" : isPrev ? "z-20 opacity-100" : "z-10 opacity-0"
              )}
            >
              <div
                className={cx(
                  "absolute inset-0 overflow-hidden [clip-path:inset(0_0_0_0)]",
                  isActive && "animate-curtain"
                )}
              >
                <div
                  className="absolute -inset-x-[4%] -inset-y-[6%] transition-transform duration-[900ms] ease-out will-change-transform"
                  style={{ transform: "translate3d(var(--px,0px), var(--py,0px), 0)" }}
                >
                  <div className={cx("absolute inset-0", isActive && "animate-lift")}>
                    <div
                      role="img"
                      aria-label={item.kicker}
                      className={cx(
                        "absolute inset-0 scale-105 bg-cover bg-center grayscale contrast-[1.12] brightness-[0.74]",
                        isActive && "animate-kenburns"
                      )}
                      style={{ backgroundImage: `url(${item.image})` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className="absolute inset-0 z-40 bg-hero-scrim" />
        <div className="absolute inset-0 z-40 bg-vignette" />
      </div>

      {/* ---------- copy ---------- */}
      <div className="relative z-10 flex w-full flex-1 items-end px-gutter pb-[clamp(20px,4vh,44px)] pt-[calc(theme(spacing.nav)+40px)]">
        <div key={slide.id} className="max-w-[min(880px,92%)]">
          <p className="mb-[clamp(14px,2.6vh,26px)] flex animate-fade-in items-center gap-3.5 text-[11px] font-medium uppercase tracking-[0.26em] text-ash-200 [animation-delay:450ms]">
            <span className="block h-px w-[46px] origin-left animate-line-x bg-current [animation-delay:500ms]" />
            {slide.kicker}
          </p>

          <h1
            className={cx(
              display,
              "mb-[clamp(16px,2.6vh,30px)] text-[clamp(46px,9.2vw,132px)]"
            )}
          >
            {slide.title.map((word, i) => (
              <span key={word + i} className="block overflow-hidden">
                <span
                  className="block animate-rise-in"
                  style={{ animationDelay: `${420 + i * 90}ms` }}
                >
                  {word}
                </span>
              </span>
            ))}
          </h1>

          <p className="max-w-[46ch] animate-fade-in text-[clamp(14px,1.55vw,17px)] leading-[1.65] text-ash-200 [animation-delay:850ms]">
            {slide.caption}
          </p>

          <div className="mt-[clamp(22px,3.4vh,38px)] flex animate-fade-in flex-wrap gap-3 [animation-delay:1000ms]">
            <a href="#projects" className={cx(btnBase, btnSolid)}>
              <span className={cx(btnSweep, btnSolidSweep)} />
              <span className={btnLabel}>View projects</span>
              <ArrowUpRight width="15" height="15" className={btnIcon} />
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer noopener"
              className={cx(btnBase, btnGhost)}
            >
              <span className={cx(btnSweep, btnGhostSweep)} />
              <span className={btnLabel}>Talk to us</span>
              <ArrowUpRight width="15" height="15" className={btnIcon} />
            </a>
          </div>
        </div>
      </div>

      {/* ---------- control band: tally · scroll cue · deck ----------
          Kept in flow rather than pinned, so it can never collide with a
          long headline on a short viewport. */}
      <div className="relative z-10 flex w-full items-end justify-between gap-6 px-gutter pb-[clamp(18px,3.5vh,40px)]">
        {/* Below md the rail and deck are gone, so the slider needs a
            visible handle of its own — otherwise four slides read as one. */}
        <ul className="flex items-center gap-2 md:hidden">
          {heroSlides.map((item, i) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to ${item.kicker}`}
                aria-current={i === index}
                className="grid h-8 place-items-center px-0.5"
              >
                <span
                  className={cx(
                    "block h-[3px] rounded-full transition-all duration-500 ease-out",
                    i === index ? "w-8 bg-paper" : "w-3 bg-white/35"
                  )}
                />
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-[18px] md:flex">
          <span className="inline-flex items-center gap-2 font-display text-[15px] tabular-nums text-ash-300">
            <em
              key={index}
              className="inline-block animate-count-in text-[30px] not-italic leading-none text-paper"
            >
              {String(index + 1).padStart(2, "0")}
            </em>
            <i className="inline-block h-px w-4 bg-ash-400" />
            {String(TOTAL).padStart(2, "0")}
          </span>

          <span className="block h-px w-[clamp(60px,9vw,150px)] overflow-hidden bg-white/15">
            <i
              key={cycle}
              className={cx(
                "block h-full origin-left scale-x-0 animate-timer bg-paper",
                paused && "[animation-play-state:paused]"
              )}
              style={{ "--dur": `${DURATION}ms` }}
            />
          </span>
        </div>

        {/* scroll cue */}
        <a
          href="#about"
          aria-label="Scroll to About"
          className="hidden flex-col items-center gap-2.5 pb-1 text-[9.5px] uppercase tracking-[0.28em] text-ash-300 md:flex xl:hidden"
        >
          <span>Scroll</span>
          <i className="relative block h-[52px] w-px overflow-hidden bg-white/15">
            <span className="absolute inset-0 animate-scroll-cue bg-paper" />
          </i>
        </a>

        {/* deck */}
        <div className="ml-auto hidden flex-col items-end gap-4 xl:flex">
          <div className="flex gap-2">
            {[
              { fn: back, label: "Previous slide", Icon: ArrowLeft },
              { fn: next, label: "Next slide", Icon: ArrowRight },
            ].map(({ fn, label, Icon }) => (
              <button
                key={label}
                type="button"
                onClick={fn}
                aria-label={label}
                className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-paper transition-colors duration-[350ms] ease-out hover:border-paper hover:bg-paper hover:text-ink"
              >
                <Icon width="17" height="17" />
              </button>
            ))}
          </div>

          <ul className="flex gap-2.5">
          {heroSlides.map((item, i) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to ${item.kicker}`}
                aria-current={i === index}
                className="group relative block w-[92px] overflow-hidden text-left"
              >
                <img
                  src={item.image}
                  alt=""
                  loading="lazy"
                  className={cx(
                    "h-[58px] w-full scale-[1.02] object-cover grayscale transition-[filter,transform] duration-700 ease-out group-hover:scale-[1.08] group-hover:brightness-100",
                    i === index ? "scale-[1.08] brightness-100" : "brightness-[0.6]"
                  )}
                />
                <span
                  className={cx(
                    "absolute inset-x-0 bottom-[22px] h-0.5 origin-left bg-paper transition-transform duration-500 ease-out",
                    i === index ? "scale-x-100" : "scale-x-0"
                  )}
                />
                <span
                  className={cx(
                    "mt-2 block text-[9.5px] uppercase tracking-[0.18em] transition-colors duration-[350ms] group-hover:text-paper",
                    i === index ? "text-paper" : "text-ash-400"
                  )}
                >
                  {item.meta}
                </span>
              </button>
            </li>
          ))}
          </ul>
        </div>
      </div>

      {/* ---------- ticker ---------- */}
      <div className="relative z-10 border-y border-white/15 bg-black/35 py-3.5 text-[11px] font-medium uppercase tracking-[0.22em] text-ash-200 backdrop-blur-sm">
        <Marquee items={marqueeWords} speed={38} />
      </div>
    </section>
  );
}
